'use client'

import React from 'react'
import { ArrowUndoIcon, BriefcaseIcon, Buildings3Icon, CalendarIcon, FileTextIcon, InformationSquareIcon } from '@navikt/aksel-icons'
import { Alert, Box, Button, CopyButton, Detail, Heading, HStack, Switch, Table, VStack } from '@navikt/ds-react'
import ArbeidIUtlandet from './_components/ArbeidIUtlandet'
import Permisjoner from './_components/Permisjoner'
import Permitteringer from './_components/Permitteringer'
import TimerMedTimeloenn from './_components/TimerMedTimeloenn'
import styles from './Arbeidsforhold.module.css'
import { store } from '@/app/_components/store/store'
import AttributtInline from '@/components/attributt-inline/AttributtInline'
import AttributtListe, { AttributtDef } from '@/components/attributt-liste/AttributtListe'
import AttributtMedIkon from '@/components/attributt-med-ikon/AttributtMedIkon'
import Divider from '@/components/divider/Divider'
import NavnIdentHeader from '@/components/navn-ident-header/NavnIdentHeader'
import { css } from '@emotion/css'
import AttributtMedTittel from 'components/attributt-med-tittel/AttributtMedTittel'
import { ArbeidsforholdDetaljert } from 'types/arbeidsforhold'
import { formatDate, formatDateText, formatTimestamp } from 'utils/formatting/dato'
import { formatFnrOrgnr, formatOrgnr } from 'utils/formatting/tall'
import { hentBeskrivelse, hentNavn, lagKopiAriaLabel, settSammenBeskrivelseKode, settSammenNavn } from 'utils/formatting/tekst'

const Arbeidsforhold = (props: { data: ArbeidsforholdDetaljert; dataminimeringValg?: string[] }) => {
    const { data, dataminimeringValg } = props

    const [visEndringerIArbeidsforholdet, setVisEndringerIArbeidsforholdet] = React.useState(false)
    const [viseDetaljerForDato, setViseDetaljerForDato] = React.useState(new Date())
    const isToday = formatDate(viseDetaljerForDato.toString()) === formatDate(new Date().toString())

    function shouldShowField(fieldName: string) {
        return dataminimeringValg ? dataminimeringValg.includes(fieldName) : true
    }

    const { tekst } = store.useReducerState()

    const sluttdato = formatDateText(data.ansettelsesperiodeSlutt, tekst._locale)
    const sluttaarsak = data.sluttaarsak?.beskrivelse

    React.useEffect(() => {
        if (!isToday) {
            setVisEndringerIArbeidsforholdet(false)
        }
    }, [isToday])

    const detaljGrupper: AttributtDef[][] = [
        [
            {
                key: 'yrke',
                title: tekst.detaljer.yrke,
                valgt: true,
                hjelpetekst: tekst.hjelpetekst.yrke,
                getValue: (data) => settSammenBeskrivelseKode(data.gjeldendeAnsettelsesdetaljer.yrke, tekst.detaljer.yrkeskode),
                getHistorikk: (historikk) => settSammenBeskrivelseKode(historikk.yrke, tekst.detaljer.yrkeskode),
            },
            {
                key: 'avtaltStillingsprosent',
                title: tekst.detaljer.avtaltStillingsprosent,
                valgt: true,
                hjelpetekst: tekst.hjelpetekst.avtaltStillingsprosent,
                getValue: (data) => data.gjeldendeAnsettelsesdetaljer.avtaltStillingsprosent?.toFixed(2).concat(' %'),
                getHistorikk: (historikk) => historikk.avtaltStillingsprosent?.toFixed(2).concat(' %'),
            },
            {
                key: 'sisteStillingsprosentendring',
                title: tekst.detaljer.sisteStillingsprosentEndring,
                valgt: shouldShowField('SISTE_STILLINGSPROSENTENDRING'),
                hjelpetekst: tekst.hjelpetekst.sisteStillingsprosentEndring,
                getValue: (data) => formatDate(data.gjeldendeAnsettelsesdetaljer.sisteStillingsprosentendring),
                getHistorikk: (historikk) => formatDate(historikk.sisteStillingsprosentendring),
            },
            {
                key: 'antallTimerPrUke',
                title: tekst.detaljer.antallTimerPrUke,
                valgt: shouldShowField('ANTALL_TIMER_PR_UKE'),
                hjelpetekst: tekst.hjelpetekst.antallTimerPrUke,
                getValue: (data) => data.gjeldendeAnsettelsesdetaljer.antallTimerPrUke,
                getHistorikk: (historikk) => historikk.antallTimerPrUke,
            },
        ],
        [
            {
                key: 'ansettelsesform',
                title: tekst.detaljer.ansettelsesform,
                valgt: shouldShowField('ANSETTELSESFORM'),
                hjelpetekst: tekst.hjelpetekst.ansettelsesform,
                getValue: (data) => hentBeskrivelse(data.gjeldendeAnsettelsesdetaljer.ansettelsesform),
                getHistorikk: (historikk) => hentBeskrivelse(historikk.ansettelsesform),
            },
            {
                key: 'arbeidstidsordning',
                title: tekst.detaljer.arbeidstidsordning,
                valgt: shouldShowField('ARBEIDSTIDSORDNING'),
                hjelpetekst: tekst.hjelpetekst.arbeidstidsordning,
                getValue: (data) => hentBeskrivelse(data.gjeldendeAnsettelsesdetaljer.arbeidstidsordning),
                getHistorikk: (historikk) => hentBeskrivelse(historikk.arbeidstidsordning),
            },
            {
                key: 'sisteLoennsendring',
                title: tekst.detaljer.sisteLonnsendring,
                valgt: shouldShowField('SISTE_LOENNSENDRING'),
                hjelpetekst: tekst.hjelpetekst.sisteLonnsendring,
                getValue: (data) => formatDate(data.gjeldendeAnsettelsesdetaljer.sisteLoennsendring),
                getHistorikk: (historikk) => formatDate(historikk.sisteLoennsendring),
            },
        ],
    ]

    if (data.type.kode == 'maritimtArbeidsforhold') {
        detaljGrupper.push([
            {
                key: 'fartsomraade',
                title: tekst.detaljer.fartsomraade,
                valgt: shouldShowField('FARTSOMRAADE'),
                hjelpetekst: tekst.hjelpetekst.fartsomraade,
                getValue: (data) => hentBeskrivelse(data.gjeldendeAnsettelsesdetaljer.fartsomraade),
                getHistorikk: (historikk) => hentBeskrivelse(historikk.fartsomraade),
            },
            {
                key: 'skipsregister',
                title: tekst.detaljer.skipsregister,
                valgt: shouldShowField('SKIPSREGISTER'),
                hjelpetekst: tekst.hjelpetekst.skipsregister,
                getValue: (data) => hentBeskrivelse(data.gjeldendeAnsettelsesdetaljer.skipsregister),
                getHistorikk: (historikk) => hentBeskrivelse(historikk.skipsregister),
            },
            {
                key: 'fartoeystype',
                title: tekst.detaljer.fartoeystype,
                valgt: shouldShowField('FARTOEYSTYPE'),
                hjelpetekst: tekst.hjelpetekst.fartoeystype,
                getValue: (data) => hentBeskrivelse(data.gjeldendeAnsettelsesdetaljer.fartoeystype),
                getHistorikk: (historikk) => hentBeskrivelse(historikk.fartoeystype),
            },
        ])
    }

    const rapporteringAttributter: AttributtDef[] = [
        {
            key: 'opprettet',
            title: tekst.detaljer.opprettet,
            valgt: true, //shouldShowField('OPPRETTET'),
            hjelpetekst: tekst.hjelpetekst.opprettet,
            getValue: (data) => formatTimestamp(data.opprettet),
        },
        {
            key: 'sistEndret',
            title: tekst.detaljer.sistEndret,
            valgt: true, //shouldShowField('SIST_ENDRET'),
            hjelpetekst: tekst.hjelpetekst.sistEndret,
            getValue: (data) => formatTimestamp(data.sistEndret),
        },
        {
            key: 'sistBekreftet',
            title: tekst.detaljer.sistBekreftet,
            valgt: true, //  shouldShowField('SIST_BEKREFTET'),
            hjelpetekst: tekst.hjelpetekst.sistBekreftet,
            getValue: (data) => formatTimestamp(data.sistBekreftet),
        },
        {
            key: 'rapporteringsordning',
            title: tekst.term.rapporteringsordning,
            valgt: true, // shouldShowField('RAPPORTERINGSORDNING'),
            hjelpetekst: tekst.hjelpetekst.rapporteringsordning,
            getValue: (data) => data.rapporteringsordning.beskrivelse,
        },
        {
            key: 'arbeidsforholdId',
            title: tekst.detaljer.arbeidsforholdId,
            valgt: shouldShowField('ARBEIDSFORHOLD_ID'),
            hjelpetekst: tekst.hjelpetekst.arbeidsforholdId,
            getValue: (data) => data.id,
        },
    ]

    return (
        <div className={styles.container}>
            <NavnIdentHeader
                ident={data.arbeidstaker.offentligIdent}
                navn={settSammenNavn(data.arbeidstaker) || ''}
                pdfUtksrift
                printData={{ type: 'arbeidsforhold', data: data }}
            />

            <Box padding={'space-24'} borderRadius={'8'} borderWidth="1" className={styles.oppsummeringBoks}>
                <HStack gap="space-16" className={styles.oppsummeringInnhold}>
                    <AttributtMedIkon
                        title={tekst.term.arbeidssted}
                        hjelpetekst={tekst.hjelpetekst.arbeidssted}
                        data={
                            <div>
                                <Detail spacing>{tekst.detaljer.underenhet}</Detail>
                                {data.arbeidssted.type === 'organisasjon'
                                    ? data.arbeidssted.organisasjonsnavn || tekst.feil.fantIkke.navn
                                    : settSammenNavn(data.arbeidssted) || tekst.feil.fantIkke.navn}
                                <br />
                                <HStack>
                                    <CopyButton
                                        variant="neutral"
                                        size="xsmall"
                                        iconPosition="right"
                                        style={{ marginLeft: '-8px' }}
                                        copyText={data.arbeidssted.offentligIdent}
                                        aria-label={lagKopiAriaLabel(data.arbeidssted.offentligIdent, tekst, hentNavn(data.arbeidssted))}
                                        text={
                                            data.arbeidssted.type === 'person'
                                                ? formatFnrOrgnr(data.arbeidssted.offentligIdent)
                                                : formatOrgnr(data.arbeidssted.offentligIdent) || tekst.detaljer.mangler
                                        }
                                    />
                                </HStack>
                            </div>
                        }
                        className={styles.oppsummeringKort}
                        icon={BriefcaseIcon}
                        valgt
                    />
                    {data.opplysningspliktig && (
                        <AttributtMedIkon
                            title={tekst.detaljer.opplysningspliktig}
                            hjelpetekst={tekst.hjelpetekst.opplysningspliktig}
                            className={styles.oppsummeringKortSmal}
                            data={
                                <div>
                                    <Detail spacing>{tekst.detaljer.hovedenhet}</Detail>
                                    {(data.opplysningspliktig.type === 'organisasjon'
                                        ? data.opplysningspliktig.organisasjonsnavn || tekst.feil.fantIkke.navn
                                        : settSammenNavn(data.opplysningspliktig)) || tekst.feil.fantIkke.navn}
                                    <br />
                                    <HStack>
                                        <CopyButton
                                            variant="neutral"
                                            size="xsmall"
                                            iconPosition="right"
                                            style={{ marginLeft: '-8px' }}
                                            copyText={data.opplysningspliktig.offentligIdent}
                                            aria-label={lagKopiAriaLabel(
                                                data.opplysningspliktig.offentligIdent,
                                                tekst,
                                                hentNavn(data.opplysningspliktig),
                                            )}
                                            text={
                                                data.opplysningspliktig.type === 'person'
                                                    ? formatFnrOrgnr(data.opplysningspliktig.offentligIdent)
                                                    : formatOrgnr(data.opplysningspliktig.offentligIdent) || tekst.detaljer.mangler
                                            }
                                        />
                                    </HStack>
                                </div>
                            }
                            icon={Buildings3Icon}
                            feilmelding={tekst.feil.fantIkke.navn}
                            valgt={true}
                        />
                    )}
                    <HStack gap="space-16" wrap={false} className={styles.datoBlokk}>
                        <CalendarIcon aria-hidden width="48px" height="48px" />
                        <VStack gap="space-8">
                            <AttributtInline
                                title={tekst.term.startdato}
                                hjelpetekst={tekst.hjelpetekst.startdato}
                                data={formatDateText(data.ansettelsesperiodeStart, tekst._locale)}
                            />
                            {/* Begge tomme betyr at personen fortsatt er ansatt. Bare én tom betyr at vi faktisk mangler data. */}
                            {(sluttdato || sluttaarsak) && (
                                <>
                                    <AttributtInline
                                        title={tekst.term.sluttdato}
                                        hjelpetekst={tekst.hjelpetekst.sluttdato}
                                        data={sluttdato ?? tekst.info.ingenData}
                                    />
                                    <AttributtInline
                                        title={tekst.detaljer.sluttaarsak}
                                        hjelpetekst={tekst.hjelpetekst.sluttaarsak}
                                        data={sluttaarsak ?? tekst.info.ingenData}
                                    />
                                </>
                            )}
                        </VStack>
                    </HStack>
                </HStack>
            </Box>
            {data.varsler &&
                data.varsler.map((varsel, idx) => (
                    <Alert variant="warning" key={idx}>
                        {varsel.varsling.beskrivelse}
                    </Alert>
                ))}
            {!isToday && (
                <Alert variant="warning" contentMaxWidth={false} className={css({ div: { width: '100%' } })}>
                    <HStack justify={'space-between'} align={'center'} gap="space-8" className={styles.datoAlert}>
                        {tekst.detaljer.viserForDato.replace('%s', formatDate(viseDetaljerForDato.toString()) ?? '')}
                        <Button
                            size="small"
                            variant="tertiary"
                            icon={<ArrowUndoIcon aria-hidden />}
                            onClick={() => setViseDetaljerForDato(new Date())}
                        >
                            {tekst.detaljer.tilbakestillDato}
                        </Button>
                    </HStack>
                </Alert>
            )}
            <Box borderWidth="1" borderRadius="8" padding="space-12" className={styles.detaljerBoks}>
                <HStack align="center" justify={'space-between'} gap="space-8" className={styles.detaljerHeader}>
                    <HStack align="center" gap="space-12" className={styles.detaljerTittel}>
                        <InformationSquareIcon aria-hidden className={styles.detaljerIkon} />
                        <Heading level="3" size="medium">
                            {tekst.detaljer.omArbeidsforholdet}
                        </Heading>
                    </HStack>
                    {isToday && (
                        <Switch className={styles.endringerBryter} onChange={(e) => setVisEndringerIArbeidsforholdet(e.target.checked)}>
                            {tekst.detaljer.visEndringer}
                        </Switch>
                    )}
                </HStack>
                <Divider className={styles.skille} />
                <Box className={styles.detaljerInnhold}>
                    <div className={styles.detaljerTabell}>
                        <Table
                            size="medium"
                            className={css({
                                width: '100%',
                                tableLayout: 'fixed',
                                '--ac-table-row-border': 'none',
                                '.aksel-table__header-cell , .aksel-table__data-cell': {
                                    boxSizing: 'content-box',
                                    padding: 'var(--ax-space-8) var(--ax-space-8)',
                                    '&:nth-child(1)': { width: '324px' }, // Etikett
                                    '&:nth-child(2)': { width: '100%' }, // Verdi
                                    borderBottom: 'none',
                                },
                                '.aksel-table__expanded-row': {
                                    backgroundColor: 'var(--ax-bg-neutral-soft)',
                                    '--ac-table-row-hover': 'var(--ax-bg-neutral-moderate-hoverA)',
                                },
                            })}
                        >
                            <Table.Body>
                                {detaljGrupper.map((gruppe, gruppeIndex) => (
                                    <React.Fragment key={gruppeIndex}>
                                        {gruppeIndex > 0 && (
                                            <tr aria-hidden="true">
                                                <td colSpan={2} className={styles.gruppeSkille}></td>
                                            </tr>
                                        )}
                                        {gruppe.map((attributt) => (
                                            <AttributtMedTittel
                                                key={attributt.key}
                                                title={attributt.title}
                                                valgt={attributt.valgt}
                                                hjelpetekst={attributt.hjelpetekst}
                                                data={data}
                                                viseDetaljerForDato={viseDetaljerForDato}
                                                visHistorikk={visEndringerIArbeidsforholdet}
                                                getValue={attributt.getValue}
                                                getHistorikk={attributt.getHistorikk}
                                            />
                                        ))}
                                    </React.Fragment>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                    <AttributtListe
                        className={styles.detaljerKortliste}
                        data={data}
                        grupper={detaljGrupper}
                        visHistorikk={visEndringerIArbeidsforholdet}
                        viseDetaljerForDato={viseDetaljerForDato}
                    />
                </Box>
            </Box>
            <Permisjoner data={data.permisjoner} />
            <Permitteringer data={data.permitteringer} />
            <ArbeidIUtlandet data={data.utenlandsopphold} />
            <TimerMedTimeloenn data={data.timerMedTimeloenn} />
            <Box borderRadius={'8'} padding="space-24" className={styles.rapporteringBoks}>
                <div className={styles.detaljerTabell}>
                    <HStack gap={'space-0'}>
                        <FileTextIcon aria-hidden fontSize="3rem" />
                        <Table
                            size="small"
                            className={css({
                                width: 'auto',
                                padding: '0px',
                                marginLeft: '-8px',
                                tableLayout: 'fixed',
                                '--ac-table-row-border': 'none',
                                '.aksel-table__header-cell , .aksel-table__data-cell': {
                                    boxSizing: 'content-box',
                                    padding: 'var(--ax-space-8) var(--ax-space-8)',
                                    borderBottom: 'none',
                                    '&:nth-child(1)': { minWidth: '322px' }, // Etikett
                                    '&:nth-child(2)': { width: '100%' }, // Verdi
                                },
                            })}
                        >
                            <Table.Body>
                                {rapporteringAttributter.map((attributt) => (
                                    <AttributtMedTittel
                                        key={attributt.key}
                                        title={attributt.title}
                                        data={data}
                                        hjelpetekst={attributt.hjelpetekst}
                                        getValue={attributt.getValue}
                                        valgt={attributt.valgt}
                                    />
                                ))}
                            </Table.Body>
                        </Table>
                    </HStack>
                </div>
                <AttributtListe className={styles.detaljerKortliste} data={data} grupper={[rapporteringAttributter]} />
            </Box>
        </div>
    )
}

export default Arbeidsforhold
